/** @module csi/SiteAdmin/ImageReplaceController */
define([
	"dojo/_base/declare",
	"underscore",
	"csi",
	"csi/_base/Deferred",
	"csi/action/Controller",
	"csi/comm/PostRequest",
	"csi/reflection",
	"csi/sessionConstants",
	"csi/state/rootStore",
	"csi/wizard/ActionControllerMixin",
	"csi/xml",
	"csi/xml/Query",

	"dijit/form/ComboButton",
	"dijit/Menu",
	"dijit/MenuItem",
	"SiteAdmin/ImageReplaceSelect",
	"SiteAdmin/ImageWhereUsedTable"
], function(declare, _, csi, Deferred, Controller, PostRequest, reflection, sessionConstants, {sessionStore}, ActionControllerMixin, xml, Query) {

	var ACTION_UNLINK = "ImageReplaceUnlink";

	return declare([Controller, ActionControllerMixin], {
		bShowWait: true,
		idWizardResource: "ImageReplace",

		/** @property {boolean} bUnlink - true if "Unlink" button was clicked (rather than "Finish" button) */
		bUnlink: false,

		/**
		 * Return true to disable Next button.
		 * Prevent user from moving to the next page until he selects new image
		 * @param {csi.wizard.Widget} wizard
		 * @param {Content} page
		 * @param {Number} index
		 * @return {boolean}
		 */
		disableNext: function(wizard, page, index) {
			var view = page.viewTab;
			return !(view.newImageUrl || view._files);
		},

		/**
		 * @override
		 * If unlinking, then only need to request images from one node; if keeping links, then it is possible that many
		 * nodes are affected and need to be redrawn. Only query for urls that are currently in the view cache.
		 */
		getReturnNodeRequest: function(urlsNew) {
			if (this.widget.hasQueryResults && !this.bUnlink) {
				// if action is called from view with many nodes, then
				var cache = this.m_cache;
				var urls = this.popupView.getChildView("ImageWhereUsed").get("store").getResults();

				urlsNew = _.filter(urls, function(url) {
					return !!cache.get(url);
				});
			} else {
				urlsNew = [this.getAction().unlinkURL];
			}

			return this.inherited(arguments, [urlsNew]);
		},

		/**
		 * Create Query filter for second page (Index=1)
		 * @param {csi.wizard.Widget} wizard
		 * @param {csi.view.Widget} view
		 * @param {Number} index
		 */
		getWizardViewQuery: function(wizard, view, index) {
			if (index == 1) {
				return new Query().Node(xml.PROP_FLAGS, xml.OP_BIT_0, xml.NODE_FLAG_FROZEN);
			}
			return null;
		},

		/**
		 * Process switching wizard pages and click on "Finish" button
		 * @param {csi/wizard/Widget} wizard
		 * @param {Content} currentPage
		 * @param {Content} nextPage
		 * @param {boolean} bFinish - indicates if this is being called while finishing wizard
		 * @return {Deferred}
		 */
		onNextPage: function(wizard, currentPage, nextPage, bFinish) {
			if (!bFinish) { // Not finish, just switching panes
				var fd = this.getFormData();
				var index = wizard.getIndexOfPage(nextPage);
				if (index == 1) { // Second page of the wizard
					// Turn off DnD on first page
					this.toggleDnD(wizard, false);
					// Resolve node for Where Used widget
					var node = fd.getNode();
					if (node) {
						var bImage = (node[xml.ATTR_TYPE] == reflection.TYPE_IMAGE);
						if (!bImage) {
							node = fd.getCache().getNode(this.getAction().m_viewRef.m_url);
							fd.setNode(node);
						}
					}
				}
			}

			return this.inherited(arguments);
		},

		/**
		 * Return to previous (first) page. Enable DnD on first page.
		 * @param {csi/wizard/Widget} wizard
		 * @param {Content} currentPage
		 * @param {Content} prevPage
		 * @return {Deferred}
		 */
		onPrevPage: function(wizard, currentPage, prevPage) {
			var index = wizard.getIndexOfPage(prevPage);
			if (index == 0) { // First page of the wizard
				// Turn ON Drag and Drop on first page
				this.toggleDnD(wizard, true);
			}
			return this.inherited(arguments);
		},

		/**
		 * Publish request to replace image
		 */
		process: function() {
			const wizard = this.popupView;
			const page1 = wizard.getPageAt(0).viewTab;
			const files = page1._files;
			const oldImageUrl = page1.oldImageUrl;
			const newImageUrl = page1.newImageUrl;
			let dfd;

			// prepare request to replace image
			const replaceReq = new PostRequest("SiteAdmin", "ReplaceImage")
				.addParameter("URL", oldImageUrl);

			if (this.bUnlink) {
				replaceReq.addParameter("ImageContainerURL", this.getAction().unlinkURL);
			}

			if (newImageUrl) {
				// image node already exists, so proceed right to replacing image
				dfd = new Deferred().resolve(newImageUrl);
			} else {
				// publish new image first, then replace image
				const req = new PostRequest("SiteAdmin", "MultiFilePublish")
					.addParameter("Prepend", true)
					.addParameter("NewNodeTypes", reflection.TYPE_IMAGE)
					.addParameter("SessionURL", sessionConstants.sessionUrl)
					.addParameter("Turntable", page1.bTurntable)
					.addParameter("URL", sessionStore.userPreferencesUrl)
					.addFiles(files, null, "FormFileFieldName");

				dfd = req.post().then(
					function(response) {
						var node = response.getResultNode();
						return node.$URL;
					}
				);
			}

			dfd = dfd.then(function(urlImage) {
				// once image URL is known, then replace image
				return replaceReq.addParameter("NewImageURL", urlImage).post();
			});

			this.waitForPublish(dfd);
		},

		/**
		 * @override
		 * Redraw view once complete
		 */
		postProcessSuccess: function() {
			const view = this.widget;
			const refNodes = view.getRefNodes();

			if (this.bUnlink || _.isEmpty(refNodes)) {
				view.redrawNode(this.refNode);
			} else {
				const urlMap = csi.hashArray(this.getModifiedUrls(), null, Boolean);

				refNodes.forEach(refNode => {
					const url = view.getRefNodeUrl(refNode);

					if (urlMap?.[url]) {
						view.redrawNode(refNode);
					}
				});
			}
		},

		/**
		 * Add "Unlink & Save" action to wizard actions (add button)
		 * @param {View} defView
		 * @param {Actions} defActions
		 */
		addProxyActions: function(defView, defActions) {
			defActions.addAction(this.getProxyAction(ACTION_UNLINK));
			this.inherited(arguments);
		},

		onLoadPopup: function(view) {
			this.inherited(arguments);
			this.toggleDnD(view, true);
		},

		/**
		 * @override
		 * Reset in case this fails and need to submit again
		 */
		onPublishError: function(err) {
			this.bUnlink = false;
		},

		/**
		 * Turn on Image DnD on first page (ImageReplaceSelect widget)
		 * @param {Object} view - Wizard's view
		 * @param {boolean} bActive
		 */
		toggleDnD: function(view, bActive) {
			view.getChildView("SelectNewImage").toggleDnD(bActive);
		}
	});
});
